import React, { useEffect, useState } from 'react';
import DropdownSingle from './DropdownSingle';
import Loader from './Loader';
import { preferencesData } from '../mockdata/common';
import { isEqual } from 'lodash';

export default function OrderPreferencesModule({
  type = 'view',
  preferences = null,
  update,
  loader = false,
  isGroupOrder = false,
}) {
  const [selectedPreferences, setSelectedPreferences] = useState({
    products: preferencesData.products[0],
    wash_temp: preferencesData.wash_temp[0],
    pickup_delivery: preferencesData.pickup_delivery[0],
    pickup_delivery_instructions: { value: '' },
    dryer_heat: preferencesData.dryer_heat[0],
    other_details: { value: '' },
  });
  useEffect(() => {
    const prefObject = preferences;
    if (prefObject && typeof prefObject === 'object' && !Array.isArray(prefObject)) {
      setSelectedPreferences({
        products: { value: prefObject.products || preferencesData.products[0].value },
        wash_temp: { value: prefObject.wash_temp || preferencesData.wash_temp[0].value },
        pickup_delivery: { value: prefObject.pickup_delivery || preferencesData.pickup_delivery[0].value },
        pickup_delivery_instructions: { value: prefObject.pickup_delivery_instructions || '' },
        dryer_heat: { value: prefObject.dryer_heat || preferencesData.dryer_heat[0].value },
        other_details: { value: prefObject.other_details || '' },
      });
      return;
    }

    // fallback: default to first option
    setSelectedPreferences({
      products: preferencesData.products[0],
      wash_temp: preferencesData.wash_temp[0],
      pickup_delivery: preferencesData.pickup_delivery[0],
      pickup_delivery_instructions: { value: '' },
      dryer_heat: preferencesData.dryer_heat[0],
      other_details: { value: '' },
    });
  }, [preferences]);

  const onPreferenceChangeFn = (typeKey, val) => {
    const value = val && (val?.value ?? val);
    if (typeKey == 'pickup_delivery') {
      //delete pickup_delivery_instructions if not 'other'
      if (value !== 'Other') {
        delete selectedPreferences['pickup_delivery_instructions'];
      } else {
        selectedPreferences['pickup_delivery_instructions'] = { value: '' };
      }
    }
    setSelectedPreferences((prev) => ({ ...prev, [typeKey]: { value } }));
  };

  const buildPreferencesObject = (prefs) => {
    const keyMap = {
      products: 'products',
      wash_temp: 'wash_temp',
      dryer_heat: 'dryer_heat',
      pickup_delivery: 'pickup_delivery',
      pickup_delivery_instructions: 'pickup_delivery_instructions',
      other_details: 'other_details',
    };
    const obj = {};
    Object.keys(prefs).forEach((key) => {
      const apiKey = keyMap[key] || key;
      const raw = prefs[key]?.value;
      if (raw === '' || raw === 'No preference' || raw === 'No Preference') obj[apiKey] = null;
      else obj[apiKey] = raw ?? null;
    });
    //if pickup_delivery is not 'other', remove pickup_delivery_instructions
    if (
      String(prefs.pickup_delivery?.value || '')
        .toLowerCase()
        .trim() !== 'other'
    ) {
      delete obj['pickup_delivery_instructions'];
    }
    return obj;
  };

  // when selectedPreferences changes in edit mode, call update with object-shaped prefs
  useEffect(() => {
    if (type === 'edit') {
      const obj = buildPreferencesObject(selectedPreferences);
      const incoming = preferences || {};
      if (!isEqual(incoming, obj)) {
        update(obj);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPreferences]);

  if (loader) return <Loader text='loading preferences...' classes='h-24' />;

  if (type === 'view') {
    const prefsObj = preferences;
    if (!prefsObj || Object.keys(prefsObj).length === 0) return <div className='go-label-16'>No preferences set</div>;

    const map = {
      products: 'Products',
      wash_temp: 'Wash Temp',
      dryer_heat: 'Dryer Heat',
      pickup_delivery: 'Pickup & Delivery',
      pickup_delivery_instructions: 'Pickup & Delivery Instructions',
      other_details: 'Other Details',
    };

    const showPickupInstructions =
      String(prefsObj.pickup_delivery || '')
        .trim()
        .toLowerCase() === 'other';

    const keys = ['products', 'wash_temp', 'dryer_heat', 'pickup_delivery'];
    if (showPickupInstructions) keys.push('pickup_delivery_instructions');
    keys.push('other_details');

    const entries = keys.map((k) => [map[k], prefsObj[k]]);

    return (
      <div>
        {entries.map(([label, value], idx) => (
          <div key={idx} className='mb-2'>
            <span className='go-label-16'>{label}: </span>
            <span className='go-textblack-16'>
              {value !== null && value !== undefined && String(value).trim() !== '' ? value : 'No preference'}
            </span>
          </div>
        ))}
      </div>
    );
  }

  // edit mode
  return (
    <div
      className={
        'grid grid-cols-1 gap-4 p-2 sm:gap-4 sm:p-3 md:mb-6 xl:grid-cols-3' + (isGroupOrder ? ' xl:grid-cols-1' : '')
      }
    >
      <div>
        <label className='go-label-16'>Products</label>
        <span className='text-red-500'>*</span>
        <DropdownSingle
          dropdownValues={preferencesData?.products}
          defaultValue={selectedPreferences?.products}
          onchange={(val) => onPreferenceChangeFn('products', val)}
        />
      </div>
      <div>
        <label className='go-label-16'>Wash Temp</label>
        <span className='text-red-500'>*</span>
        <DropdownSingle
          dropdownValues={preferencesData?.wash_temp}
          defaultValue={selectedPreferences?.wash_temp}
          onchange={(val) => onPreferenceChangeFn('wash_temp', val)}
        />
      </div>
      <div>
        <label className='go-label-16'>Dryer Heat</label>
        <span className='text-red-500'>*</span>
        <DropdownSingle
          dropdownValues={preferencesData?.dryer_heat}
          defaultValue={selectedPreferences?.dryer_heat}
          onchange={(val) => onPreferenceChangeFn('dryer_heat', val)}
        />
      </div>
      <div>
        <label className='go-label-16'>Pickup and Delivery</label>
        <span className='text-red-500'>*</span>
        <DropdownSingle
          dropdownValues={preferencesData?.pickup_delivery}
          defaultValue={selectedPreferences?.pickup_delivery}
          onchange={(val) => onPreferenceChangeFn('pickup_delivery', val)}
        />
      </div>
      {String(selectedPreferences?.pickup_delivery?.value || '')
        .toLowerCase()
        .includes('other') && (
        <div>
          <label htmlFor='pickup_delivery_instructions' className='go-label-16'>
            Pickup / Delivery Instructions
          </label>
          <span className='text-red-500'>*</span>
          {(() => {
            const pickupValue = selectedPreferences?.pickup_delivery_instructions?.value || '';
            const showError = pickupValue.trim().length < 5;
            return (
              <>
                <textarea
                  id='pickup_delivery_instructions'
                  className={'form-textarea go-inputtext-16' + (showError ? ' border-red-500' : '')}
                  rows='4'
                  required
                  aria-invalid={showError ? 'true' : 'false'}
                  aria-describedby={showError ? 'pickup-instructions-error' : undefined}
                  value={pickupValue}
                  onChange={(e) => {
                    const val = e.target.value;
                    setSelectedPreferences((prev) => ({ ...prev, pickup_delivery_instructions: { value: val } }));
                  }}
                ></textarea>
                {showError && (
                  <p id='pickup-instructions-error' className='go-textnormal-14 text-red-500'>
                    Minimum 5 characters required.
                  </p>
                )}
              </>
            );
          })()}
        </div>
      )}
      <div>
        <label htmlFor='other_details' className='go-label-16'>
          Other Details
        </label>
        <textarea
          id='other_details'
          className='form-textarea go-inputtext-16'
          rows='4'
          required
          value={selectedPreferences?.other_details?.value}
          onChange={(e) => setSelectedPreferences((prev) => ({ ...prev, other_details: { value: e.target.value } }))}
        ></textarea>
      </div>
    </div>
  );
}
